import React, { ReactEventHandler, useEffect, useRef, useState } from 'react'
import { get, isEmpty, sumBy } from 'lodash-es'
import { subscribe } from 'react-contextual'
import classNames from 'classnames'
import { Helmet } from 'react-helmet'
import { map, pathOr, pipe, sum } from 'ramda'
import { withFormik } from 'formik'
import * as yup from 'yup'
import { format } from 'date-fns'
import { arrayUtils, currencyUtils } from '../../../../../utils'
import { InvoicesProvider } from '../../../../providers'
import {
  Button,
  ControlledTooltip,
  DatePicker,
  FormLoader,
  InfoBox,
  InvoiceCard,
  Loader,
  Segment,
} from '../../../../components'
import FormErrors from '../../../molecules/FormErrors/FormErrors'
import TaskList from '../../../../components/atoms/Svgs/TaskList'
import CalendarOff from '../../../../components/atoms/Svgs/CalendarOff'
import PaymentAllocation from './PaymentAllocation/PaymentAllocation'
import styles from './ViewInvoice.module.scss'
import { hashCode } from 'utils/string'
import { invoiceApiEvents } from '../../../../../modules/api/invoice'
import FundDistributionFooter from '../../../organisms/FormFragments/FundDistribution/FundDistributionFooter/FundDistributionFooter'
import DocumentInfo from 'modules/documents/DocumentInfo/DocumentInfo'
import { $TSFixMe } from 'types/ts-migrate'
import { matchPath } from 'react-router-dom'
import RouterPromptModal from 'views/components/organisms/Modals/RouterPromptModal/RouterPromptModal'
import { transformDepositBeneficiaryForTransferChange } from 'views/components/organisms/FormFragments/FundDistribution/DepositBeneficiary/depositBeneficiaryUtils'
import { getDepositBeneficiaryAmount } from 'modules/api/invoice/utils'
import { existingInvoiceTypes } from 'utils/invoiceTypes'
import { ROUTES } from 'constants/routes'

interface ViewInvoiceProps {
  invoice?: $TSFixMe
  sendInvoice?: (...args: any[]) => void
  updateInvoice: (id: string, payload: $TSFixMe) => void
  dueDate?: string
  // Injected by the InvoicesProvider subscription
  params?: $TSFixMe
  redirect: (path: string) => void
  currentInvoice?: $TSFixMe
  isDraftRemoved?: boolean
  deleteInvoices: (ids: string[]) => void
  restoreInvoices: (ids: string[]) => void
  editingInvoice?: boolean
  fetchInvoiceTypes: () => void
  invoiceTypes: $TSFixMe[]
  closeInvoice: (id: string, tab: string, open?: boolean) => void
  getPartyNameById: (id: string) => string
  getInvoiceTypeByValue: (value: string) => $TSFixMe
  openInvoice: (id: string, tab: string, open?: boolean) => void
  partyTags?: $TSFixMe
  saveAndSendInvoice: (id: string, values: $TSFixMe) => void
  isDraftInvoiceViewLoading?: boolean
  isSaveAndSendSubmitting?: boolean
  isSaveSubmitting?: boolean
  /** True for the whole "Send now" submit, save leg included — see the container for why */
  isSaveAndSendInFlight?: boolean
  getGeneralFormErrorsByEvent: (event: $TSFixMe) => $TSFixMe[]
  getFormFieldErrorsByEvent: (event: $TSFixMe) => $TSFixMe
  isReadOnly?: boolean
  currentAgencyId?: string
  getOwnerAccountFromDraftInvoiceId: (id: string) => $TSFixMe
  getCurrentAgencyGlobalVatEnabled?: boolean
  fetchPortfolio: (id: string) => void
  // Injected by withFormik
  values: $TSFixMe
  setFieldValue: (field: string, value: $TSFixMe, shouldValidate?: boolean) => void
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void
  validateForm: () => Promise<$TSFixMe>
  errors: $TSFixMe
  dirty: boolean
}

const validationSchema = yup.object().shape({
  beneficiaries: yup
    .array(
      yup
        .object()
        .nullable()
        .shape({
          beneficiary: yup
            .object()
            .nullable()
            .shape({
              type: yup.string(),
              value: yup.object().shape({
                amount: yup.number(),
                /** @todo don't think this is needed */
                balance: yup.number(),
                // beneficiaryCategory: yup.string(),
                id: yup.string(),
                partyId: yup.string(),
                partyTag: yup.string(),
                reference: yup.string(),
                vat: yup.boolean(),
              }),
            }),
        }),
    )
    .test('beneficiaries', function (beneficiaries) {
      const validBeneficiaries = beneficiaries?.filter((b: any) => b !== null && b !== undefined) || []
      const beneficiariesTotal = sumBy(validBeneficiaries, (b: any) => b.beneficiary.value.amount)
      const activeElementPlaceholder = document?.activeElement?.getAttribute('placeholder')
      if (beneficiariesTotal > this.parent.amount && activeElementPlaceholder !== 'Enter amount') {
        return this.createError({
          path: this.path,
          message:
            this.parent.tags.lightStatus === 'Amber'
              ? `Beneficiary payments can't exceed invoice total of ${
                  currencyUtils.formatCurrency(this.parent.amount) as string
                }`
              : `Beneficiary payments can't exceed invoice total of ${
                  currencyUtils.formatCurrency(this.parent.balance) as string
                }`,
        })
      }
      return true
    }),
})

/**
 * A draft deposit invoice's beneficiary amount is derived from the invoice amount, so that a
 * deposit created with the beneficiary amount left at zero isn't shown as unallocated.
 */
const withDerivedDepositAmount = (invoice: $TSFixMe): $TSFixMe => {
  if (!Array.isArray(invoice?.beneficiaries)) return invoice

  return {
    ...invoice,
    beneficiaries: invoice.beneficiaries.map((b: $TSFixMe) =>
      !b?.beneficiary?.value
        ? b
        : {
            ...b,
            beneficiary: {
              ...b.beneficiary,
              value: {
                ...b.beneficiary.value,
                amount: getDepositBeneficiaryAmount({
                  invoiceType: invoice.invoiceType,
                  invoiceAmount: invoice.amount,
                  beneficiaryType: b.beneficiary.type,
                  amount: b.beneficiary.value.amount,
                }),
              },
            },
          },
    ),
  }
}

const ViewInvoice = (props: ViewInvoiceProps): null | React.ReactElement => {
  const {
    editingInvoice,
    currentInvoice: invoice,
    isDraftRemoved,
    isDraftInvoiceViewLoading,
    isSaveAndSendSubmitting,
    isSaveSubmitting,
    isSaveAndSendInFlight,
    isReadOnly,
    handleSubmit,
    validateForm,
    values,
    setFieldValue,
    getPartyNameById,
    partyTags,
    errors,
    dirty,
    dueDate,
    params,
    redirect,
    updateInvoice,
    deleteInvoices,
    restoreInvoices,
    closeInvoice,
    openInvoice,
    fetchInvoiceTypes,
    invoiceTypes,
    getInvoiceTypeByValue,
    getGeneralFormErrorsByEvent,
    getFormFieldErrorsByEvent,
    saveAndSendInvoice,
    currentAgencyId,
    getOwnerAccountFromDraftInvoiceId,
    getCurrentAgencyGlobalVatEnabled,
    fetchPortfolio,
  } = props

  const [beneficiariesHash, setBeneficiariesHash] = useState<number | null>(null)
  const isMounted = useRef(false)
  const prevProps = useRef({ dueDate, currentInvoice: invoice, isSaveAndSendSubmitting, params })

  const handleClose = (): void => {
    redirect(`/invoices/drafts`)
  }

  const handleContextMenuItemSelect = (item: any): void => {
    if (item === 'delete') {
      deleteInvoices([invoice.id])
      closeInvoice(values.id, 'drafts')
    } else if (item === 'restore') {
      restoreInvoices([invoice.id])
      closeInvoice(values.id, 'drafts')
    }
  }

  /**
   * The `invoiceUpdate_request` a save dispatches, used to look its API errors back up. Null until
   * the invoice has loaded, as `values` is only populated once `currentInvoice` is in the store.
   */
  const buildUpdateEvent = (): $TSFixMe => {
    if (!values?.id) return null
    const { id, ...payload } = values
    return invoiceApiEvents.invoiceUpdate_request({ id, values: payload })
  }

  const saveDraft = async (): Promise<boolean> => {
    const validationErrors = await validateForm()
    if (!isEmpty(validationErrors)) return false
    const { id = false, ...payload } = values
    if (!id) return false
    updateInvoice(id, payload)
    return true
  }

  const handleSave: ReactEventHandler = e => {
    e.preventDefault()
    void saveDraft()
  }

  const getInvoiceStatus = (): string => get(props, 'values.tags.subStatus', '').toLowerCase()

  const handleNewBeficiary = (beneficiary: any, index: number): void => {
    const vatApplied =
      existingInvoiceTypes.includes(values.invoiceType) &&
      beneficiary.beneficiary.value.partyTag === 'Agency' &&
      getCurrentAgencyGlobalVatEnabled
        ? true
        : false
    setFieldValue(
      'beneficiaries',
      arrayUtils.insert(
        index,
        {
          ...beneficiary,
          beneficiary: {
            ...beneficiary.beneficiary,
            value: {
              ...beneficiary.beneficiary.value,
              vat: vatApplied,
            },
          },
        },
        values.beneficiaries,
      ),
    )
  }

  const handleBeneficiaryRemoved = (index: number): void => {
    setFieldValue('beneficiaries', arrayUtils.removeByIndex(index, values.beneficiaries))
  }

  const handleBeneficiaryAmountChange = (beneficiaryIndex: number, e: any): void => {
    const beneficiary = values.beneficiaries.find((v: any, i: any) => i === beneficiaryIndex)
    setFieldValue(`beneficiaries[${beneficiaryIndex}]`, {
      ...beneficiary,
      beneficiary: {
        ...beneficiary.beneficiary,
        value: {
          ...beneficiary.beneficiary.value,
          amount: e.target.value ? e.target.value : 0,
        },
      },
    })
  }

  const handleDepositBeneficiaryTransferChange = (index: number, transfer: any): void => {
    const beneficiary = values.beneficiaries.find((v: any, i: any) => i === index)
    const portfolioOwnerAccount = getOwnerAccountFromDraftInvoiceId(values.id)
    const invoiceTypeObj = getInvoiceTypeByValue(values.invoiceType || '')
    const invoiceTypeName = pathOr('', ['name'], invoiceTypeObj)

    const transformedBeneficiary = transformDepositBeneficiaryForTransferChange(
      beneficiary,
      transfer,
      currentAgencyId || '',
      values?.customer.name,
      invoiceTypeName,
      portfolioOwnerAccount?.partyId,
    )

    // When toggling to managed-by-reOS (transfer: true), reset amount to invoice amount
    const updatedBeneficiary =
      transfer && beneficiary?.beneficiary?.value?.amount !== values.amount
        ? {
            ...transformedBeneficiary,
            beneficiary: {
              ...transformedBeneficiary.beneficiary,
              value: {
                ...transformedBeneficiary.beneficiary.value,
                amount: values.amount,
              },
            },
          }
        : transformedBeneficiary

    setFieldValue(`beneficiaries[${index}]`, updatedBeneficiary)
  }

  const handleBeneficiaryOrderChange = (newOrder: any): void => {
    const updatedBeneficiaries = newOrder
      .map((o: any) => {
        return values.beneficiaries[o]
      })
      .filter((b: any) => b !== null && b !== undefined)
    setFieldValue('beneficiaries', updatedBeneficiaries)
    setBeneficiariesHash(hashCode(JSON.stringify(updatedBeneficiaries)))
    // updateInvoice()
  }

  const getBeneficiariesAllocationsSum = (): number =>
    pipe(pathOr([], ['beneficiaries']), map(pathOr(0, ['beneficiary', 'value', 'amount'])), sum)(values)

  const getUnallocatedAmount = (): number => {
    const currentInvoiceAmount = pathOr(null, ['currentInvoice', 'amount'], props)
    if (values.tags.lightStatus === 'Amber' || values.tags.lightStatus === 'Green') {
      return (values.amount || 0) - getBeneficiariesAllocationsSum()
    } else {
      return currentInvoiceAmount ? currentInvoiceAmount - getBeneficiariesAllocationsSum() || 0 : 0
    }
  }

  const isAmountValid = (): boolean => {
    if (values) {
      const { amount } = values
      return parseFloat(amount) > 0
    }
    return false
  }

  const handleDescriptionChange = ({ target }: any): void => {
    setFieldValue('description', target.value)
  }

  const canSave = dirty && isEmpty(errors)

  const ButtonContainer = ({ children }: $TSFixMe): React.ReactElement => {
    return (
      <footer>
        <Button type="button" ghost={!canSave} disabled={!canSave} onClick={handleSave} size="sm">
          Save
        </Button>
        {isDraftRemoved ? null : !isAmountValid() ? (
          <ControlledTooltip body="No amount set">
            <Button disabled ghost type="button" size="sm" as="div">
              <span>Send now</span>
            </Button>
          </ControlledTooltip>
        ) : (
          children
        )}
      </footer>
    )
  }

  const DocumentUploads = (): React.ReactElement | false =>
    invoice?.id && (
      <Segment.Group>
        <div className={styles['files-section']}>
          <DocumentInfo className="invoice" classId={invoice.id} modalTitle={invoice.customer.name} />
        </div>
      </Segment.Group>
    )

  /**
   * "Save" dispatches `invoiceUpdate_request` while "Send now" dispatches `sendInvoices_request`,
   * and API errors are keyed to the event that caused them. Read both, otherwise a rejected save
   * fails silently: the panel stays dirty with no indication of what went wrong.
   */
  const getApiErrors = (): $TSFixMe => {
    const submitEvents = [invoiceApiEvents.sendInvoices_request([values?.id]), buildUpdateEvent()].filter(Boolean)
    const generalErrors = submitEvents.flatMap(event => getGeneralFormErrorsByEvent(event))
    const fieldErrors = submitEvents.reduce((errors, event) => ({ ...errors, ...getFormFieldErrorsByEvent(event) }), {})
    const hasApiErrors = generalErrors.length > 0 || !isEmpty(fieldErrors)
    return { generalErrors, fieldErrors, hasApiErrors }
  }

  const areAllFundsAllocated = (): boolean => getUnallocatedAmount() === 0

  const buildFundDistributionFooter = (): React.ReactElement => {
    const unallocatedAmount = getUnallocatedAmount()
    return (
      <FundDistributionFooter
        text={'Amount unallocated'}
        amount={unallocatedAmount}
        isComplete={areAllFundsAllocated()}
        progress={{
          // Guard the divide: an unset invoice amount would otherwise render the radial as NaN
          value: values.amount ? 100 - (unallocatedAmount / values.amount) * 100 : 0,
          style: 'default',
        }}
      />
    )
  }

  const getBeneficiaries = (): $TSFixMe => get(props, 'values.beneficiaries', [])

  const handleBeneficiaryReferenceChange = (beneficiaryIndex: number, reference: any, type: any): void => {
    const beneficiary = getBeneficiaries().find((v: any, i: any) => i === beneficiaryIndex)

    setFieldValue(`beneficiaries[${beneficiaryIndex}]`, {
      ...beneficiary,
      beneficiary: {
        ...beneficiary.beneficiary,
        value: {
          ...beneficiary.beneficiary.value,
          ...(type === 'EasyPayBeneficiary' ? { easyPayReference: reference } : { reference }),
        },
      },
    })
  }

  const pathname = get(location, 'pathname', '')

  useEffect(() => {
    const isInvoiceRoute = matchPath(pathname, { path: ROUTES.invoiceView })
    fetchInvoiceTypes()
    if (invoice && isInvoiceRoute) {
      fetchPortfolio(invoice.portfolioId)
    }
    setBeneficiariesHash(hashCode(JSON.stringify(get(props, 'values.beneficiaries', ''))))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice])

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true
      prevProps.current = { dueDate, currentInvoice: invoice, isSaveAndSendSubmitting, params }
      return
    }
    const prev = prevProps.current

    if (prev.dueDate !== dueDate) {
      setFieldValue('dueDate', dueDate)
    }

    const { hasApiErrors } = getApiErrors()
    const id = get(props, 'values.id')
    const wasNotSet =
      get(prev.currentInvoice, 'tags.lightStatus', '') === 'Amber' && get(invoice, 'tags.lightStatus', '') === 'Green'
    if (wasNotSet && id && prev.isSaveAndSendSubmitting && !isSaveAndSendSubmitting && !hasApiErrors) {
      closeInvoice(id, 'drafts')
    }

    // open invoice. Required for the case when user navigates to the invoice
    if (params?.id && prev.params?.id !== params.id) {
      openInvoice(params.id, 'drafts', false)
    }
    // close invoice. Required for the case when user navigates away from the invoice
    if (prev.params?.id && !params?.id) {
      closeInvoice(prev.params.id, 'drafts', false)
    }

    prevProps.current = { dueDate, currentInvoice: invoice, isSaveAndSendSubmitting, params }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dueDate, invoice, isSaveAndSendSubmitting, params, values])

  const classes = classNames(styles.root, {
    editing: editingInvoice,
  })

  const customerName = pathOr('', ['customer', 'name'], values)

  const { hasApiErrors, generalErrors } = getApiErrors()

  const formLoaderState = isDraftInvoiceViewLoading
    ? 'loading'
    : isSaveAndSendSubmitting || isSaveSubmitting
    ? 'submitting'
    : !isEmpty(errors) || hasApiErrors
    ? 'error'
    : 'clear'

  const handleAmountChange = (e: $TSFixMe): void => {
    const value = e.target.value || 0
    setFieldValue('amount', value)
    if (
      values.beneficiaries.length === 1 &&
      (values.tags.lightStatus === 'Amber' || values.tags.lightStatus === 'Green')
    ) {
      setFieldValue('beneficiaries[0].beneficiary.value.amount', value, false)
    }
  }

  return (
    <div className={classes}>
      {/**
       * `history.getUserConfirmation` is a deliberate no-op (see src/state/history.ts), so
       * react-router's `Prompt` would block navigation without ever asking — leaving links
       * out of this panel doing nothing while the draft has unsaved changes. Use the custom
       * modal that actually prompts instead, and let the beneficiary's "Add now" bank
       * details shortcut navigate straight through, as it does on an active invoice.
       */}
      <RouterPromptModal
        when={dirty}
        isValid={isEmpty(errors)}
        isSubmitting={isSaveSubmitting || isSaveAndSendInFlight}
        allowPathname={pathname => Boolean(matchPath(pathname, { path: ROUTES.editContact }))}
        onSave={saveDraft}
        onDiscard={() => true}
      />
      {!isEmpty(values) && (
        <Helmet>
          <title>reOS | Invoices | Drafts | {`${customerName as string} - ${values.invoiceTypeName as string}`}</title>
        </Helmet>
      )}
      <InvoiceCard
        invoice={invoice}
        contextMenuOptions={
          isDraftRemoved
            ? [
                {
                  label: 'Restore deleted invoice',
                  onSelect: () => handleContextMenuItemSelect('restore'),
                  isHidden: isReadOnly ?? false,
                },
              ]
            : [
                {
                  label: 'Delete invoice',
                  onSelect: () => handleContextMenuItemSelect('delete'),
                  isHidden: isReadOnly ?? false,
                },
              ]
        }
        onClose={handleClose}
      >
        {isDraftInvoiceViewLoading ? (
          <Loader />
        ) : (
          <FormLoader
            onSubmit={handleSubmit}
            state={formLoaderState}
            buttonProps={{ children: isDraftRemoved ? '' : 'Send now' }}
            ButtonContainer={ButtonContainer}
            alwaysEnabledChildren={DocumentUploads}
            isDisabled={isReadOnly}
          >
            {isDraftRemoved && (
              <InfoBox className={styles['deleted-info']} type="info">
                This invoice was deleted.{' '}
                <Button size="sm" onClick={() => handleContextMenuItemSelect('restore')}>
                  Restore deleted invoice
                </Button>
              </InfoBox>
            )}
            <section className={styles['invoice-data']}>
              {!isEmpty(values) && (
                <>
                  <Segment.Group>
                    <Segment>
                      <Segment.Label icon={<TaskList />} text={customerName} secondaryText={values.property} />
                    </Segment>
                  </Segment.Group>
                  <FormErrors errors={generalErrors} />
                  <PaymentAllocation
                    errors={errors}
                    invoiceId={values.id}
                    invoiceType={{ name: values.invoiceTypeName, value: values.invoiceType }}
                    amount={values.amount ? values.amount.toString() : ''}
                    amountVatApplied={values.vat}
                    onAmountChange={handleAmountChange}
                    onVatChange={(vat: any) => setFieldValue('vat', vat)}
                    onBeneficiaryVatChange={(index: number, vat: any) =>
                      setFieldValue(`beneficiaries[${index}].beneficiary.value.vat`, vat)
                    }
                    onDepositBeneficiaryTransferChange={handleDepositBeneficiaryTransferChange}
                    beneficiaries={values.beneficiaries.map((b: any) => {
                      const partyId = pathOr('', ['beneficiary', 'value', 'partyId'], b)
                      return {
                        ...b,
                        beneficiary: {
                          ...b.beneficiary,
                          value: {
                            ...b.beneficiary.value,
                            name: getPartyNameById(partyId),
                          },
                        },
                      }
                    })}
                    invoiceStatus={getInvoiceStatus()}
                    onNewBeneficiaryAdded={handleNewBeficiary}
                    onBeneficiaryRemoved={handleBeneficiaryRemoved}
                    onNewBeneficiaryAmountChange={handleBeneficiaryAmountChange}
                    onBeneficiariesOrderChange={handleBeneficiaryOrderChange}
                    onBeneficiaryReferenceChange={handleBeneficiaryReferenceChange}
                    partyTags={partyTags}
                    beneficiariesKey={beneficiariesHash}
                    description={values.description}
                    onDescriptionChange={handleDescriptionChange}
                    unallocatedAmount={getUnallocatedAmount()}
                    Footer={buildFundDistributionFooter}
                    portfolioId={values.portfolioId}
                  />

                  <Segment.Group>
                    <Segment horizontal>
                      <Segment.Label icon={<CalendarOff />} text="Payment Due" />
                      <div className={styles['due-date']}>
                        <DatePicker
                          date={values.dueDate ? new Date(values.dueDate) : new Date()}
                          onChange={(date: any) => setFieldValue('dueDate', format(date, 'yyyy-MM-dd'))}
                        />
                      </div>
                    </Segment>
                  </Segment.Group>
                </>
              )}
            </section>
          </FormLoader>
        )}
      </InvoiceCard>
    </div>
  )
}

export default subscribe(
  [InvoicesProvider],
  ({
    params,
    redirect,
    currentInvoice,
    isDraftRemoved,
    updateInvoice,
    sendInvoices,
    deleteInvoices,
    restoreInvoices,
    editingInvoice,
    fetchInvoiceTypes,
    invoiceTypes,
    closeInvoice,
    getPartyNameById,
    getInvoiceTypeByValue,
    openInvoice,
    partyTags,
    agencyBankDetails,
    getPartyBankDetails,
    fetchPortfolio,
    saveAndSendInvoice,
    isInvoiceViewLoading,
    isDraftInvoiceViewLoading,
    isSaveAndSendSubmitting,
    isSaveSubmitting,
    isSaveAndSendInFlight,
    getGeneralFormErrorsByEvent,
    getFormFieldErrorsByEvent,
    isReadOnly,
    currentAgencyId,
    getOwnerAccountFromDraftInvoiceId,
    getPartyAccount,
    getCurrentAgencyGlobalVatEnabled,
  }: $TSFixMe) => ({
    params,
    redirect,
    currentInvoice,
    isDraftRemoved,
    updateInvoice,
    sendInvoices,
    deleteInvoices,
    restoreInvoices,
    editingInvoice,
    fetchInvoiceTypes,
    invoiceTypes,
    closeInvoice,
    getPartyNameById,
    getInvoiceTypeByValue,
    openInvoice,
    partyTags,
    agencyBankDetails,
    getPartyBankDetails,
    fetchPortfolio,
    saveAndSendInvoice,
    isInvoiceViewLoading,
    isDraftInvoiceViewLoading,
    isSaveAndSendSubmitting,
    isSaveSubmitting,
    isSaveAndSendInFlight,
    getGeneralFormErrorsByEvent,
    getFormFieldErrorsByEvent,
    isReadOnly,
    currentAgencyId,
    getOwnerAccountFromDraftInvoiceId,
    getPartyAccount,
    getCurrentAgencyGlobalVatEnabled,
  }),
)(
  withFormik({
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    enableReinitialize: true,
    mapPropsToValues: (props: $TSFixMe) => withDerivedDepositAmount(props.currentInvoice) ?? {},
    handleSubmit: (values: $TSFixMe, { props }: $TSFixMe) => {
      props.saveAndSendInvoice(values.id, values)
    },
  })(ViewInvoice),
)
